import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type GridReadyEvent,
  type ICellRendererParams
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo, useRef } from 'react';
import { useEntryById } from 'src/features/entries/hooks';
import { IMatchingEntry } from 'src/ts/interfaces';
import {
  CenteredCheckContainer,
  GridCellContainer,
  GridCellStyle,
  GridDocButton,
  GridStyle,
  MatchBadge,
  MatchesGridContainer
} from './styles';

ModuleRegistry.registerModules([AllCommunityModule]);

interface MatchesDataGridProps {
  matches: IMatchingEntry[];
  onSelectionChanged: (selectedIds: string[]) => void;
}

const ApplicantCell: FC<ICellRendererParams<IMatchingEntry>> = ({ data }) => {
  const { entry: entryDetails } = useEntryById(data!.entry_id);
  const name = entryDetails?.applicant?.full_name || 'Unknown';
  return <span title={name}>{name}</span>;
};

const PropertyCell: FC<ICellRendererParams<IMatchingEntry>> = ({ data }) => {
  const { entry: entryDetails } = useEntryById(data!.entry_id);
  const property = entryDetails?.folder?.property?.name || 'Unknown Property';
  return <span title={property}>{property}</span>;
};

const EmailCell: FC<ICellRendererParams<IMatchingEntry>> = ({ data }) => {
  const { entry: entryDetails } = useEntryById(data!.entry_id);
  const email = entryDetails?.applicant?.email || 'No email';
  return <span title={email}>{email}</span>;
};

const PhoneCell: FC<ICellRendererParams<IMatchingEntry>> = ({ data }) => {
  const { entry: entryDetails } = useEntryById(data!.entry_id);
  const phone = entryDetails?.applicant?.phone || 'No phone';
  return <span title={phone}>{phone}</span>;
};

const AppliedCell: FC<ICellRendererParams<IMatchingEntry>> = ({ data }) => {
  const { entry: entryDetails } = useEntryById(data!.entry_id);
  const hasAppliedBefore = entryDetails?.has_previously_submitted;

  return hasAppliedBefore ? (
    <CenteredCheckContainer>
      <img src="/assets/images/blueCheck.svg" alt="Applied Before" width={20} height={20} />
    </CenteredCheckContainer>
  ) : null;
};

const MatchCell: FC<ICellRendererParams<IMatchingEntry>> = ({ data }) => {
  const percentage = data?.percentage;
  const getMatchBadgeColor = (p?: number) => {
    if (!p) return '#6b7280';
    if (p >= 90) return 'rgba(209, 250, 229, 1)';
    if (p >= 70) return '#FEF3C7';
    return '#FCC';
  };
  return (
    <GridCellContainer>
      <MatchBadge color={getMatchBadgeColor(percentage)}>
        {percentage ? `${percentage}% Match` : 'Unknown'}
      </MatchBadge>
    </GridCellContainer>
  );
};

const DocCell: FC<ICellRendererParams<IMatchingEntry>> = ({ data }) => {
  const { entry: entryDetails } = useEntryById(data!.entry_id);

  const handleDocumentClick = () => {
    if (entryDetails?.folder_id) {
      window.open(`/folder/${entryDetails.folder_id}`, '_blank');
    }
  };

  return (
    <GridCellContainer>
      <GridDocButton
        onClick={handleDocumentClick}
        title="Open folder review page"
        aria-label="Open folder review page"
      >
        <img src="/assets/images/doc.svg" alt="" width={24} height={24} />
      </GridDocButton>
    </GridCellContainer>
  );
};

const MatchesDataGrid: FC<MatchesDataGridProps> = ({ matches, onSelectionChanged }) => {
  const gridRef = useRef<AgGridReact<IMatchingEntry>>(null);

  const columnDefs = useMemo<ColDef<IMatchingEntry>[]>(
    () => [
      {
        headerName: '',
        checkboxSelection: true,
        headerCheckboxSelection: false,
        width: 56,
        pinned: 'left'
      },
      {
        headerName: 'Applicant name',
        colId: 'applicant',
        width: 180,
        cellRenderer: ApplicantCell
      },
      {
        headerName: 'Property name',
        colId: 'property',
        width: 140,
        cellRenderer: PropertyCell
      },
      {
        headerName: 'Applied Before',
        width: 135,
        cellRenderer: AppliedCell,
        cellClass: 'center-cell'
      },
      {
        headerName: 'Email',
        width: 220,
        colId: 'email',
        cellRenderer: EmailCell
      },
      {
        headerName: 'Phone',
        width: 150,
        colId: 'phone',
        cellRenderer: PhoneCell
      },
      {
        headerName: '',
        width: 100,
        cellRenderer: MatchCell,
        headerClass: 'center-header',
        cellClass: 'center-cell',
        cellStyle: GridCellStyle as any
      },
      {
        headerName: '',
        width: 60,
        cellRenderer: DocCell,
        headerClass: 'center-header',
        cellClass: 'center-cell',
        cellStyle: GridCellStyle as any
      }
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true
    }),
    []
  );

  const onGridReady = (params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  };

  return (
    <MatchesGridContainer className="ag-theme-quartz" style={GridStyle as React.CSSProperties}>
      <AgGridReact<IMatchingEntry>
        ref={gridRef}
        rowData={matches}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowSelection="multiple"
        suppressRowClickSelection
        getRowId={(p) => p.data.entry_id}
        suppressRowHoverHighlight={true}
        suppressCellFocus={true}
        suppressHorizontalScroll={true}
        onGridReady={onGridReady}
        onSelectionChanged={() => {
          const selected = (gridRef.current?.api.getSelectedRows() || []) as IMatchingEntry[];
          const selectedIds = selected.map((r) => r.entry_id);
          onSelectionChanged(selectedIds);
        }}
        rowHeight={40}
        headerHeight={40}
        pagination={false}
        suppressScrollOnNewData={true}
        domLayout="autoHeight"
      />
    </MatchesGridContainer>
  );
};

export default MatchesDataGrid;
